import { useState } from 'react'
import { useTenants, useAuth } from '@/hooks/useApi'
import { useTenantContext } from '@/contexts/TenantContext'
import { DataTable } from '@/components/ui/data-table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Plus, Trash2, Settings, Brain, Coins } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { AddTenantModal } from '@/components/AddTenantModal'
import { TenantSettingsModal } from '@/components/TenantSettingsModal'
import { SmsCreditsModal } from '@/components/SmsCreditsModal'
import AiSettingsPanel from '@/components/AiSettingsPanel'

export function Tenants() {
  const { tenants, loading, error, refetch } = useTenants()
  const { user, isSuperAdmin } = useAuth()
  const { setSelectedTenantId } = useTenantContext()
  
  // All useState hooks MUST come before any conditional returns
  const [deleting, setDeleting] = useState<string | null>(null)
  const [showAddModal, setShowAddModal] = useState(false)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const [showAiSettingsModal, setShowAiSettingsModal] = useState(false)
  const [showCreditsModal, setShowCreditsModal] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<any>(null)
  
  // Prevent access to Tenants page from tenant subdomains
  const hostname = window.location.hostname
  const isSubdomain = hostname.endsWith('.queuemein.com') && 
    !['admin.queuemein.com', 'api.queuemein.com', 'queuemein.com', 'admin.dev.queuemein.com', 'admin.test.queuemein.com'].includes(hostname)
  
  if (isSubdomain || !isSuperAdmin) {
    return (
      <div className="space-y-6">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900">Access Denied</h1>
          <p className="text-gray-600 mt-2">Tenant management is only available to super administrators on the main admin portal.</p>
        </div>
      </div>
    )
  }
  
  const handleTenantSelect = (tenantId: string) => {
    setSelectedTenantId(tenantId)
  }

  // Subscription loading removed to prevent infinite re-renders

  const handleDelete = async (tenantId: string) => {
    if (!confirm(`Delete tenant ${tenantId}? This cannot be undone.`)) return
    
    try {
      setDeleting(tenantId)
      await apiClient.deleteTenant(tenantId)
      refetch()
    } catch (err) {
      alert('Failed to delete tenant')
    } finally {
      setDeleting(null)
    }
  }

  // handleCancelSubscription temporarily removed

  const columns = [
    { 
      key: 'display_name' as const, 
      header: 'Display Name',
      render: (_: string, row: any) => {
        const tenantId = row.tenant_id || (row.pk ? row.pk.split('#')[0].replace('t_', '') : '')
        const displayName = row.display_name || tenantId
        return (
          <button 
            className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer"
            onClick={() => handleTenantSelect(tenantId)}
          >
            {displayName}
          </button>
        )
      }
    },
    { 
      key: 'tenant_id' as const, 
      header: 'Tenant ID',
      render: (_: string, row: any) => {
        const tenantId = row.tenant_id || (row.pk ? row.pk.split('#')[0].replace('t_', '') : '')
        return tenantId
      }
    },
    { 
      key: 'status' as const, 
      header: 'Status',
      render: (value: string) => (
        <Badge variant={value === 'active' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      )
    },
    {
      key: 'plan' as const,
      header: 'Plan',
      render: (value: string, row: any) => {
        // Use the plan data that's already in the tenant object
        const plan = row.plan || value
        const trialEndDate = row.trial_end_date
        
        if (plan === 'trial' && trialEndDate) {
          return <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">14-Day Trial</Badge>
        }
        
        return <Badge variant="outline">{plan || 'Unknown'}</Badge>
      }
    },
    {
      key: 'actions' as const,
      header: 'Actions',
      render: (_: string, row: any) => {
        const tenantId = row.tenant_id || (row.pk ? row.pk.split('#')[0].replace('t_', '') : '')
        return (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedTenant(row)
                setShowSettingsModal(true)
              }}
            >
              <Settings className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedTenant(row)
                setShowAiSettingsModal(true)
              }}
              className="border-blue-500 text-blue-600 hover:bg-blue-50"
            >
              <Brain className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setSelectedTenant(row)
                setShowCreditsModal(true)
              }}
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              <Coins className="h-4 w-4" />
            </Button>
            <Button
              variant="destructive"
              size="sm"
              onClick={() => handleDelete(tenantId)}
              disabled={deleting === tenantId}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )
      }
    }
  ]

  if (loading) return <div>Loading tenants...</div>
  if (error) return <div>Error: {error}</div>

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Tenant Management</h1>
          <p className="text-gray-600 mt-1">Manage tenants, their subscriptions, and billing status</p>
        </div>
        {user?.groups?.includes('SUPER_ADMIN') && (
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add Tenant
          </Button>
        )}
      </div>

      <div className="bg-white rounded-lg border p-6">
        <div className="mb-4 text-sm text-gray-600">
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">14-Day Trial</Badge>
              <span>Trial Period</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="default" className="bg-green-50 text-green-700 border-green-200">Professional</Badge>
              <span>Active Subscription</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge variant="destructive">Cancelled</Badge>
              <span>Cancelled Subscription</span>
            </div>
          </div>
        </div>
        <DataTable 
          data={tenants} 
          columns={columns}
          searchable
          searchPlaceholder="Search tenants..."
        />
      </div>
      
      <AddTenantModal 
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={() => {
          refetch()
          // Trigger a custom event to refresh tenant dropdown
          window.dispatchEvent(new CustomEvent('tenants-updated'))
        }}
      />
      
      <TenantSettingsModal
        isOpen={showSettingsModal}
        onClose={() => {
          setShowSettingsModal(false)
          setSelectedTenant(null)
        }}
        tenant={selectedTenant}
        onSuccess={() => {
          refetch()
          setShowSettingsModal(false)
          setSelectedTenant(null)
        }}
      />
      
      <AiSettingsPanel
        tenantId={selectedTenant?.tenant_id || (selectedTenant?.pk ? selectedTenant.pk.split('#')[0].replace('t_', '') : '')}
        isVisible={showAiSettingsModal}
        onClose={() => {
          setShowAiSettingsModal(false)
          setSelectedTenant(null)
        }}
        isSuperAdmin={user?.groups?.includes('SUPER_ADMIN') || false}
      />
      
      <SmsCreditsModal
        isOpen={showCreditsModal}
        onClose={() => {
          setShowCreditsModal(false)
          setSelectedTenant(null)
        }}
        tenant={selectedTenant}
        onSuccess={() => {
          refetch()
        }}
      />
    </div>
  )
}